/** OIDC identity claims, as returned by `OAuthService.getIdentityClaims()`. */
export type IdentityClaims = Record<string, unknown> | null | undefined;

/** Resolved account identity, used to populate the auth store. */
export interface ResolvedIdentity {
  userId: string;
  displayName: string;
  email: string;
}

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * Derive the display identity from OIDC claims. Curity (scope `openid`) does not
 * release dedicated `email`/`name` claims: the address arrives as `email` or
 * `preferred_username`, and the name only as given/family parts (standard
 * `given_name`/`family_name` or the custom `first_name`/`last_name`).
 *
 * `sub` is an opaque identifier, never an email — it is used only for `userId`
 * and as a last-resort display label, so a UUID can never surface as an address.
 */
export function resolveIdentity(claims: IdentityClaims): ResolvedIdentity {
  const c = claims ?? {};
  const email = asString(c['email'] ?? c['preferred_username']);
  const fullName = [c['given_name'] ?? c['first_name'], c['family_name'] ?? c['last_name']]
    .map(asString)
    .filter(Boolean)
    .join(' ');
  const displayName = asString(c['name']) || fullName || email || asString(c['sub']);
  return { userId: asString(c['sub']), displayName, email };
}
